import * as THREE from "three";
import Orchestrator from "../Orchestrator.js";
import { device } from "../Utils/device.js";
import { CONFIG, surfaceY } from "./config.js";
import Sky from "./Sky.js";
import Terrain from "./Terrain.js";
import TerrainCollider from "./TerrainCollider.js";
import Atmosphere from "./Atmosphere.js";
import Robot from "./Robot.js";
import Debris from "./Debris.js";
import Sandstorm from "./Sandstorm.js";
import Hud from "./Hud.js";
import Input from "./Input.js";
import PlayerController from "./PlayerController.js";
import TerrainEditor from "./terrain/editor/TerrainEditor.js";

export default class Stage {
    constructor() {
        this.orc = new Orchestrator();
        this.scene = this.orc.scene;
        this.camera = this.orc.camera;
        this.debug = this.orc.debug;

        this.ready = false;

        this.orc.emitter.on("ready", () => {
            this.setup();
        });
    }

    setup() {
        if (this.setupDone) return;
        this.setupDone = true;

        if (device.isTouchDevice) {
            document.body.classList.add("touch-device");
        }

        this.scene.background = new THREE.Color(0x05060a);
        this.scene.fog = new THREE.FogExp2(0xc4845a, 0.00135);
        this.setLights();

        this.sky = new Sky();
        this.updateLights();
        this.terrain = new Terrain();
        this.terrainCollider = new TerrainCollider(this.terrain);
        this.stormParams = {
            fadeIn: 8,
            fadeOut: 3,
            preview: false,
            previewIntensity: 1,
        };
        const stormFolder = this.debug.active
            ? this.debug.ui.addFolder("Sandstorm")
            : null;
        this.setStormDebug(stormFolder);
        this.atmosphere = new Atmosphere({
            sky: this.sky,
            terrainMaterial: this.terrain.material,
            scene: this.scene,
            camera: this.camera.instance,
            debug: this.debug,
            stormFolder,
        });
        this.robot = new Robot();
        this.robot.group.visible = false;
        this.debris = new Debris();
        this.sandstorm = new Sandstorm(stormFolder);
        this.hud = new Hud({ onRestart: () => this.reset() });
        this.input = new Input({
            onDig: () => {
                if (this.ready) this.dig();
            },
            onReset: () => {
                if (this.ready) this.reset();
            },
        });
        this.playerController = new PlayerController({
            input: this.input,
            terrainCollider: this.terrainCollider,
            model: this.robot.group,
        });
        this.editor = null;

        this.gameState = "playing";
        this.health = 100;
        this.stormTimer = CONFIG.STORM_PERIOD;
        this.stormActive = false;
        this.stormLeft = 0;
        this.stormIntensity = 0;
        this.stormBlend = 0;

        if (this.debug.active) {
            this.debug.ui.domElement.style.top = "78px";
            if (this.orc.stats) this.orc.stats.dom.style.top = "78px";
        }

        this.orc.emitter.on("terrain-ready", () => {
            void this.onTerrainReady();
        });
    }

    async onTerrainReady() {
        if (this.ready) return;

        this.robot.group.visible = true;
        const spawn = this.terrain.layout.spawn;
        this.terrainCollider.rebuild(spawn.x | 0, spawn.z | 0);
        await this.playerController.init();
        this.reset();
        this.ready = true;
        if (this.debug.active) this.editor = new TerrainEditor(this.terrain);
    }

    setLights() {
        this.lightParams = {
            hemiDay: 0.9,
            hemiNight: 0.2,
            sunDay: 0.95,
            sunNight: 0.1,
            fillDay: 0.25,
            fillNight: 0.1,
        };

        this.hemi = new THREE.HemisphereLight(0xffc9a0, 0x140803, 0.9);
        this.sun = new THREE.DirectionalLight(0xffdcb8, 0.95);
        this.sun.position.set(14, 24, 10);
        this.fill = new THREE.DirectionalLight(0x6688ff, 0.25);
        this.fill.position.set(-10, 6, -12);
        this.scene.add(this.hemi, this.sun, this.fill);

        this._hemiSkyDay = new THREE.Color(0xffc9a0);
        this._hemiSkyNight = new THREE.Color(0x1a1210);
        this._hemiGroundDay = new THREE.Color(0x140803);
        this._hemiGroundNight = new THREE.Color(0x080301);

        this.setLightDebug();
    }

    setLightDebug() {
        if (!this.debug.active) return;

        const folder = this.debug.ui.addFolder("Lighting");
        const params = this.lightParams;
        folder.add(params, "hemiNight", 0, 1, 0.01).name("night hemi");
        folder.add(params, "sunNight", 0, 1, 0.01).name("night sun");
        folder.add(params, "fillNight", 0, 1, 0.01).name("night fill");
    }

    setStormDebug(folder) {
        if (!folder) return;

        const params = this.stormParams;
        folder.add(params, "preview").name("preview (visual only)");
        folder
            .add(params, "previewIntensity", 0, 1, 0.01)
            .name("preview intensity");
        const actions = {
            startNow: () => {
                if (!this.stormActive) this.stormTimer = 0;
            },
        };
        folder.add(actions, "startNow").name("start storm now");

        const timing = folder.addFolder("Timing");
        timing.add(CONFIG, "STORM_PERIOD", 5, 120, 1).name("period (s)");
        timing.add(CONFIG, "STORM_DURATION", 1, 60, 1).name("duration (s)");
        timing.add(CONFIG, "STORM_DPS", 0, 50, 0.5).name("damage per second");
        timing.add(params, "fadeIn", 0.5, 20, 0.5).name("fade in (s)");
        timing.add(params, "fadeOut", 0.5, 20, 0.5).name("fade out (s)");
    }

    stormVisualIntensity() {
        const params = this.stormParams;
        return params.preview ? params.previewIntensity : this.stormIntensity;
    }

    updateLights() {
        const day = 1 - this.sky.getNight();
        const params = this.lightParams;
        this.hemi.intensity = THREE.MathUtils.lerp(
            params.hemiNight,
            params.hemiDay,
            day,
        );
        this.sun.intensity = THREE.MathUtils.lerp(
            params.sunNight,
            params.sunDay,
            day,
        );
        this.fill.intensity = THREE.MathUtils.lerp(
            params.fillNight,
            params.fillDay,
            day,
        );
        this.hemi.color.lerpColors(this._hemiSkyNight, this._hemiSkyDay, day);
        this.hemi.groundColor.lerpColors(
            this._hemiGroundNight,
            this._hemiGroundDay,
            day,
        );
    }

    playerDepth() {
        const state = this.playerController.state;
        return this.terrain.depthAt(state.i, state.j);
    }

    dig() {
        if (!this.ready || this.gameState !== "playing") return;
        if (!this.playerController.isGrounded) return;

        const state = this.playerController.state;
        const dugY = this.terrain.heightAt(state.i, state.j);
        const expectedY = surfaceY(dugY);
        const footY = this.playerController.position.y;
        if (Math.abs(footY - expectedY) > CONFIG.DIG_HEIGHT_TOLERANCE) return;

        if (!this.terrain.isSolid(state.i, state.j, dugY)) return;
        if (!this.terrain.dig(state.i, state.j)) return;

        const collisionMesh = this.terrainCollider.rebuild(state.i, state.j);
        this.playerController.replaceTerrainCollider(collisionMesh);
        this.debris.spawn(state.i, state.j, dugY);
        state.digs++;
        this.checkWin();
    }

    checkWin() {
        const state = this.playerController.state;
        const depth = this.playerDepth();
        if (
            this.gameState === "playing" &&
            this.playerController.isGrounded &&
            depth >= CONFIG.WIN_DEPTH
        ) {
            this.gameState = "won";
            this.playerController.setEnabled(false);
            this.hud.showResult({
                failed: false,
                title: "任务成功！",
                sub: "已下潜 " + depth + " 米 · 共挖掘 " + state.digs + " 次",
            });
        }
    }

    lose() {
        if (this.gameState !== "playing") return;
        this.gameState = "lost";
        this.health = 0;
        this.playerController.setEnabled(false);
        this.hud.showResult({
            failed: true,
            title: "任务失败",
            sub: "生命值耗尽 —— 沙尘暴中不要待在地表！",
        });
    }

    reset() {
        if (!this.terrain.ready) return;

        this.terrain.reset();
        if (this.playerController.initialized) {
            const spawn = this.terrain.layout.spawn;
            const collisionMesh = this.terrainCollider.rebuild(
                spawn.x | 0,
                spawn.z | 0,
            );
            this.playerController.replaceTerrainCollider(collisionMesh);
        }
        this.debris.clear();
        this.playerController.reset();

        this.gameState = "playing";
        this.health = 100;
        this.stormTimer = CONFIG.STORM_PERIOD;
        this.stormActive = false;
        this.stormLeft = 0;
        this.stormIntensity = 0;
        this.stormBlend = 0;
        this.sandstorm.reset();
        this.atmosphere.update(
            this.stormVisualIntensity(),
            this.playerController.position,
        );

        this.input.clear();
        this.hud.hideResult();
        this.hud.update({
            depth: this.playerDepth(),
            health: this.health,
            stormActive: this.stormActive,
            stormLeft: this.stormLeft,
            stormTimer: this.stormTimer,
            stormIntensity: this.stormIntensity,
        });
    }

    updateStorm(dt) {
        const fadeIn = Math.max(this.stormParams.fadeIn, 0.05);
        const fadeOut = Math.max(this.stormParams.fadeOut, 0.05);

        this.stormTimer -= dt;
        if (!this.stormActive && this.stormTimer <= 0) {
            this.stormActive = true;
            this.stormLeft = CONFIG.STORM_DURATION;
            this.stormTimer = CONFIG.STORM_PERIOD;
        }

        if (this.stormActive) {
            this.stormLeft -= dt;
            if (this.stormLeft <= 0) this.stormActive = false;
        }

        // The rise runs through the end of the calm and is complete when the
        // storm starts, so the full storm stays full for its whole duration.
        if (this.stormActive) {
            this.stormBlend = Math.min(1, this.stormBlend + dt / fadeIn);
        } else {
            this.stormBlend = Math.max(0, this.stormBlend - dt / fadeOut);
            if (this.stormTimer <= fadeIn) {
                const rise = 1 - Math.max(this.stormTimer, 0) / fadeIn;
                if (rise > this.stormBlend) this.stormBlend = rise;
            }
        }

        this.stormIntensity = THREE.MathUtils.smoothstep(this.stormBlend, 0, 1);
    }

    updateHealth(dt) {
        if (this.gameState !== "playing") return;

        const atSurface = this.playerDepth() === 0;
        if (this.stormActive && atSurface) {
            this.health -= CONFIG.STORM_DPS * dt;
            if (this.health <= 0) {
                this.health = 0;
                this.lose();
            }
        }
    }

    // both time params are measured in seconds
    update(elapsed, delta) {
        if (!this.ready) return;

        const dt = Math.min(delta, 0.05);
        if (!this.camera.flying) {
            this.playerController.update(dt);
        }
        this.updateStorm(dt);
        this.updateHealth(dt);
        this.terrain.update(this.camera.instance);
        this.debris.update(dt);
        this.sky.update(dt, elapsed);
        this.updateLights();
        const stormIntensity = this.stormVisualIntensity();
        this.atmosphere.update(stormIntensity, this.playerController.position);
        this.sandstorm.update(
            dt,
            this.playerController.position,
            stormIntensity,
            this.atmosphere.nightScale,
        );
        this.editor?.update();
        this.hud.update({
            depth: this.playerDepth(),
            health: this.health,
            stormActive: this.stormActive,
            stormLeft: this.stormLeft,
            stormTimer: this.stormTimer,
            stormIntensity,
        });
    }
}
